import { Checkbox } from '@ark-ui/react/checkbox';
import { type FC } from 'react';

import { AGENT_MARK_FALLBACK } from '@/components/agent-marks';

import { InviteCheckboxControl } from './invite-checkbox';

interface InviteAgentOptionProps {
  checked: boolean;
  /** Inline SVG source. Undefined falls back to the generic mark. */
  mark?: string;
  onCheckedChange: (checked: boolean) => void;
  title: string;
}

/**
 * One agent in the panel's access list. Figma 11930:194558.
 *
 * Three states there — default, hover and selected — and the selected one is
 * the only place the row is branded: brand-light ground, brand-green border.
 * Hover moves the border to secondary and leaves the ground alone.
 *
 * A whole-row label rather than a box with text beside it, so the pointer
 * target is the row the frame draws. Checkbox.Root renders a <label>, which is
 * what makes the title click through to the control without a `htmlFor`.
 */
export const InviteAgentOption: FC<InviteAgentOptionProps> = ({
  checked,
  mark,
  onCheckedChange,
  title,
}) => (
  <Checkbox.Root
    checked={checked}
    className={[
      'invite-agent-row flex w-full cursor-pointer items-center gap-2 rounded-lg border px-4 py-3',
      'transition-colors duration-[120ms] ease-[ease]',
      checked
        ? 'border-[var(--ds-color-bg-brand)] bg-[var(--ds-color-bg-brand-light)]'
        : 'border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] hover:border-[var(--ds-color-border-secondary)]',
    ].join(' ')}
    onCheckedChange={({ checked: next }) => onCheckedChange(next === true)}
  >
    <Checkbox.HiddenInput />

    {/* SVG source strings, so dangerouslySetInnerHTML is the only way in — an
        <img src> would drop the .ds-agent-logo classes.
        data-ds-agent-logo-inline is load-bearing: it is the only rule in the
        design system's Logos.css supplying --ds-color-text-invert, and the
        glyphs are currentColor, so without it they render dark on the
        dark-green tile. The 40px the class sizes them to is taken down to the
        frame's 24 in invite-users-panel.css, which needs a descendant selector
        this component cannot write as a utility. */}
    <span
      aria-hidden
      className="invite-agent-mark flex shrink-0 items-center"
      dangerouslySetInnerHTML={{ __html: mark ?? AGENT_MARK_FALLBACK }}
      data-ds-agent-logo-inline
    />

    <Checkbox.Label className="min-w-0 flex-1 truncate text-sm leading-5 text-[var(--ds-color-text-primary)]">
      {title}
    </Checkbox.Label>

    <InviteCheckboxControl />
  </Checkbox.Root>
);
